/**
 * AsyncCart.js (Complete version with all fixes)
 * Refactored Cart class with proper async handling and cleaner discount logic
 */

import EventEmitter from './EventEmitter.js';
import DiscountEngine from './DiscountEngine.js';
import ConditionValidator from './ConditionValidator.js';

export default class AsyncCart extends EventEmitter {
    constructor(cartData = {}, logger = null) {
        super(logger);

        // Initialize with defaults
        const defaults = {
            items: [],
            discounts: [],
            subtotal: 0,
            base_subtotal: 0,
            itemCount: 0,
            tax: 0,
            taxes: 0,
            total: 0,
            actual_subtotal: 0,
            minimumAmount: 0,
            minimumAmountMet: true,
            minimumAmountNeeded: 0,
        };

        Object.assign(this, defaults, cartData);

        // Initialize engines
        this.discountEngine = new DiscountEngine(this.logger);
        this.conditionValidator = new ConditionValidator(this.logger);

        // State cache
        this._cache = new Map();
        this._isUpdating = false;
        }

        // Add item to cart
        async addItem(item) {
        if (this._isUpdating) {
            await this.waitForUpdate();
        }

        const normalizedItem = this.normalizeItem(item);
        if (!normalizedItem) {
            throw new Error('Invalid item data');
        }

        const existingItem = this.getItem(normalizedItem.item_id);

        if (existingItem) {
            // Merge with existing item
            existingItem.quantity += normalizedItem.quantity;
            this.mergeAddons(existingItem, normalizedItem.addons);
        } else {
            // Add new item
            this.items.push(normalizedItem);
        }

        await this.updateCartTotals();
        return true;
    }

    // Set all items at once
    async setItems(items = []) {
    if (this._isUpdating) {
        await this.waitForUpdate();
    }

    this.items = [];

    for (const item of items) {
        const normalizedItem = this.normalizeItem(item);
        if (normalizedItem) {
        this.items.push(normalizedItem);
        }
    }

    await this.updateCartTotals();
    }

    // Update item properties
    async updateItem(itemId, updatedProperties) {
    if (this._isUpdating) {
        await this.waitForUpdate();
    }

    const item = this.getItem(itemId);
    if (!item) {
        throw new Error(`Item with ID ${itemId} not found`);
    }

    Object.assign(item, updatedProperties);
    await this.updateCartTotals();
    return true;
    }

    // Remove item from cart
    async removeItem(itemId) {
    if (this._isUpdating) {
        await this.waitForUpdate();
    }

    const index = this.items.findIndex(item => item.item_id === itemId);
    if (index === -1) {
        throw new Error(`Item with ID ${itemId} not found`);
    }

    this.items.splice(index, 1);
    await this.updateCartTotals();
    return true;
    }

    // Async method to add discount with proper validation
    async addDiscount(discount) {
    if (this._isUpdating) {
        await this.waitForUpdate();
    }

    const normalizedDiscount = this.normalizeDiscount(discount);

    // Check for duplicate
    if (this.discountAdded(normalizedDiscount.id || normalizedDiscount.promo_code)) {
        throw new Error(`Discount with ID ${normalizedDiscount.id || normalizedDiscount.promo_code} has already been applied`);
    }

    // Validate the discount asynchronously
    const validation = await this.conditionValidator.validateDiscount(
        normalizedDiscount,
        await this.getCartState()
    );

    if (!validation.valid) {
        throw new Error(`Cannot add discount: ${validation.reason}`);
    }

    // Check combinability rules
    if (!await this.canAddDiscount(normalizedDiscount)) {
        throw new Error('Discount conflicts with existing discounts');
    }

    this.discounts.push(normalizedDiscount);
    await this.updateCartTotals();

    return true;
    }

    // Enhanced setDiscounts method with priority and conflict resolution
    async setDiscounts(discounts = []) {
    this._isUpdating = true;

    try {
        const normalized = discounts
        .map(d => this.normalizeDiscount(d))
        .filter(Boolean);

        // Get current cart state for validation
        const cartState = await this.getCartState();

        // Validate all discounts and check conditions
        const validatedDiscounts = [];
        for (const discount of normalized) {
        const validation = await this.conditionValidator.validateDiscount(discount, cartState);
        if (validation.valid) {
            // Also check if discount conditions are met
            const conditionsMet = await this.validateDiscountConditions(discount, cartState);
            if (conditionsMet) {
            validatedDiscounts.push(discount);
            }
        }
        }

        // Sort by priority first (higher priority = higher number = first)
        const sortedByPriority = validatedDiscounts.sort((a, b) => {
        const priorityA = this.discountEngine.getNumericPriority(a) || 0;
        const priorityB = this.discountEngine.getNumericPriority(b) || 0;
        return priorityB - priorityA; // Higher priority first
        });

        // Apply non-combinable logic with priority consideration
        const finalDiscounts = this.resolveDiscountConflicts(sortedByPriority);

        this.discounts = finalDiscounts;
        await this.updateCartTotals();
    } finally {
        this._isUpdating = false;
    }
    }

    // Enhanced method to validate discount conditions
    async validateDiscountConditions(discount, cartState) {
    if (!discount.conditions || Object.keys(discount.conditions).length === 0) {
        return true;
    }

    const conditionResults = [];

    for (const [condId, condition] of Object.entries(discount.conditions)) {
        const result = await this.checkSingleCondition(condition, cartState);
        conditionResults.push(result);
    }

    if (discount.requirement === 'all') {
        return conditionResults.every(result => result);
    } else {
        return conditionResults.some(result => result);
    }
    }

    // Check a single condition
    async checkSingleCondition(condition, cartState) {
    const categorySummary = cartState.categorySummary;
    let categoryData = categorySummary[condition.service];

    // If not found by service name, try by service_id
    if (!categoryData && condition.service_id) {
        categoryData = Object.values(categorySummary).find(category =>
            category.items && category.items.some(item =>
            String(item.category_id) === String(condition.service_id)
            )
        );
    }

    if (!categoryData) {
        return false;
    }

    const actualQuantity = categoryData.quantity || 0;
    const requiredQuantity = parseInt(condition.qty) || 0;

    switch (condition.condition) {
        case 'less_than':
        return actualQuantity < requiredQuantity;
        case 'equal_to':
        return actualQuantity >= requiredQuantity;
        case 'at_least':
        default:
        return actualQuantity >= requiredQuantity;
    }
    }

    // Resolve discount conflicts considering priority and non-combinable rules.
    //
    // A non-combinable discount ("cannot be applied with others") is exclusive:
    //  - It blocks any other discount that overlaps one of the services it covers.
    //  - It blocks all order-level discounts (an order-level discount applies to the
    //    whole order, including the non-combinable discount's services).
    // Discounts on services the non-combinable discount does NOT cover are left alone.
    //
    // The set of blocked services is computed up front from ALL surviving non-combinable
    // discounts, so the outcome no longer depends on the order in which the customer
    // applied the promos. `sortedDiscounts` is pre-sorted by priority (descending), so
    // when two non-combinable discounts overlap the higher-priority one wins.
    resolveDiscountConflicts(sortedDiscounts) {
    const blockedServices = new Set();
    const survivingNonCombinable = new Set();
    let hasNonCombinable = false;

    // Pass 1: let non-combinable discounts claim their services (priority order).
    for (const discount of sortedDiscounts) {
        if (!this.discountEngine.isNonCombinable(discount)) continue;

        const services = this.discountEngine
        .getDiscountServices(discount)
        .filter(service => service !== 'ORDER_LEVEL');

        // Skip if a higher-priority non-combinable discount already claimed one of these services.
        if (services.some(service => blockedServices.has(service))) {
        this.logger.log(`Non-combinable discount ${discount.promo_code} skipped; overlaps a higher-priority non-combinable discount`);
        continue;
        }

        survivingNonCombinable.add(discount);
        services.forEach(service => blockedServices.add(service));
        hasNonCombinable = true;
        this.logger.log(`Non-combinable discount ${discount.promo_code} blocking services:`, services);
    }

    // Pass 2: build the final list in priority order, honoring the blocks above.
    const result = [];
    for (const discount of sortedDiscounts) {
        if (this.discountEngine.isNonCombinable(discount)) {
        if (survivingNonCombinable.has(discount)) {
            result.push(discount);
        }
        continue;
        }

        // Order-level discounts cannot combine with any non-combinable discount.
        if (discount.type === 'order' && hasNonCombinable) {
        this.logger.log(`Order-level discount ${discount.promo_code} blocked by non-combinable discount`);
        continue;
        }

        // Category/item discounts are blocked if they overlap a claimed service.
        const services = this.discountEngine
        .getDiscountServices(discount)
        .filter(service => service !== 'ORDER_LEVEL');

        if (services.some(service => blockedServices.has(service))) {
        this.logger.log(`Discount ${discount.promo_code} blocked; overlaps a non-combinable discount`);
        continue;
        }

        result.push(discount);
    }

    return result;
    }

    // Remove discount
    async removeDiscount(discountId) {
    if (this._isUpdating) {
        await this.waitForUpdate();
    }

    const index = this.discounts.findIndex(d => d.id === discountId || d.promo_code === discountId);
    if (index === -1) {
        throw new Error(`Discount with ID ${discountId} not found`);
    }

    this.discounts.splice(index, 1);
    await this.updateCartTotals();
    return true;
    }

    // Async cart totals calculation
    async updateCartTotals() {
    if (this._isUpdating) return;

    this._isUpdating = true;

    try {
        // Emit pre-calculation event
        await this.emit('beforeUpdate', this);

        // Reset totals
        this.itemCount = 0;
        this.base_subtotal = 0;
        this.subtotal = 0;
        this.actual_subtotal = 0;

        if (this.items.length === 0) {
        await this.emit('afterUpdate', this);
        return;
        }

        // Calculate base values (synchronous)
        this.calculateBaseValues();

        // Calculate discounts (asynchronous)
        const discountResults = await this.calculateAllDiscountsAsync();

        // Apply final calculations
        this.applyFinalCalculations(discountResults);

        // Clear cache and emit events
        this.clearCache();
        await this.emit('afterUpdate', this);

    } finally {
        this._isUpdating = false;
    }
    }

    // Fixed calculateBaseValues method
    calculateBaseValues() {
    let subtotal = 0;
    let discountableBase = 0;
    let minimumAmountTotal = 0;

    this.items.forEach(item => {
        item.base_price = item.price_per_unit * item.quantity;
        item.subtotal = item.base_price;

        this.itemCount += item.quantity;
        subtotal += item.base_price;

        if (item.discountable !== false) {
        discountableBase += item.base_price;
        }

        // Fixed logic: only include in minimum if exclude_from_min is NOT true
        if (!item.exclude_from_min) {
        minimumAmountTotal += item.base_price;
        }

        // Process addons
        item.addons?.forEach(addon => {
        addon.base_price = addon.addon_price * addon.quantity;
        addon.subtotal = addon.base_price;
        subtotal += addon.base_price;

        if (addon.discountable !== false) {
            discountableBase += addon.base_price;
        }

        // Fixed logic: only include in minimum if exclude_from_min is NOT true
        if (!addon.exclude_from_min) {
            minimumAmountTotal += addon.base_price;
        }
        });
    });

    this.base_subtotal = discountableBase;
    this.actual_subtotal = subtotal;
    this.minimumAmountMet = minimumAmountTotal >= this.minimumAmount;

    // Store the amount that counts toward minimum for later use
    this._minimumAmountTotal = minimumAmountTotal;
    }

    async calculateAllDiscountsAsync() {
    const appliedDiscounts = [];
    let totalDiscount = 0;

    // Get current cart state
    const cartState = await this.getCartState();

    // Filter applicable discounts asynchronously
    const applicableDiscounts = [];
    for (const discount of this.discounts) {
        const validation = await this.conditionValidator.validateDiscount(discount, cartState);
        if (validation.valid) {
        applicableDiscounts.push(discount);
        }
    }

    // Process each discount
    for (const discount of applicableDiscounts) {
        try {
        const discountResult = await this.calculateSingleDiscountAsync(discount, cartState);
        if (discountResult.discount_amount > 0) {
            totalDiscount += discountResult.discount_amount;
            appliedDiscounts.push(discountResult);
        }
        } catch (error) {
        this.logger.error(`Error calculating discount ${discount.promo_code}:`, error);
        }
    }

    return { totalDiscount, appliedDiscounts };
    }

    // Enhanced calculateSingleDiscountAsync with fixed complex discount support
    async calculateSingleDiscountAsync(discount, cartState) {
    let totalDiscountAmount = 0;
    let affectedItems = [];

    // Handle discounts with specific prices for different services
    if (discount.prices && Object.keys(discount.prices).length > 0) {

        // First, apply the specific pricing rules from the prices field
        for (const [serviceId, priceConfig] of Object.entries(discount.prices)) {
        const applicableItems = this.getItemsForService(serviceId, cartState);

        if (applicableItems.length > 0) {
            // Check if conditions are met for this service
            const condition = discount.conditions?.[serviceId];
            if (condition && await this.checkSingleCondition(condition, cartState)) {

            // Calculate discount for this specific service using its price config
            const serviceDiscountAmount = await this.calculateServiceDiscount(
                applicableItems,
                priceConfig,
                condition
            );

            totalDiscountAmount += serviceDiscountAmount;

            if (serviceDiscountAmount > 0) {
                const serviceName = priceConfig.service || `Service ${serviceId}`;
                const totalQty = applicableItems.reduce((sum, item) => sum + item.quantity, 0);
                affectedItems.push(`${totalQty} ${serviceName} (${priceConfig.amount}% OFF)`);
            }
            }
        }
        }

        // Then, handle services that don't have specific prices (these get the main discount amount/type)
        const servicesWithPrices = new Set(Object.keys(discount.prices));

        // Find services in the discount category that don't have specific prices
        if (discount.category) {
        for (const categoryName of discount.category) {
            const categoryItems = cartState.items.filter(item => item.category === categoryName);

            if (categoryItems.length > 0) {
            // Find the service_id for this category
            const serviceId = categoryItems[0].category_id || categoryItems[0].service_term;

            // Only process if this service doesn't have specific pricing
            if (!servicesWithPrices.has(String(serviceId))) {
                const condition = discount.conditions?.[serviceId];

                if (condition && await this.checkSingleCondition(condition, cartState)) {

                // Apply the main discount (amount_type and amount from the discount)
                const freeAmount = await this.calculateMainDiscountForService(
                    categoryItems,
                    discount,
                    condition
                );

                totalDiscountAmount += freeAmount;

                if (freeAmount > 0) {
                    const qty = this.getApplicableQuantity(categoryItems, condition);
                    if (discount.amount_type === 'percent' && discount.amount === 100) {
                    affectedItems.push(`${qty} ${categoryName} (FREE)`);
                    } else {
                    affectedItems.push(`${qty} ${categoryName} (${discount.amount}${discount.amount_type === 'percent' ? '%' : ''} OFF)`);
                    }
                }
                }
            }
            }
        }
        }

    } else {
        // Handle regular discounts without complex pricing
        totalDiscountAmount = await this.calculateRegularDiscount(discount, cartState);
        if (totalDiscountAmount > 0) {
        affectedItems.push('Applied to eligible items');
        }
    }

    return {
        id: discount.id,
        code: discount.promo_code,
        title: discount.title || discount.promo_code,
        type: discount.amount_type,
        amount: discount.amount,
        discount_amount: totalDiscountAmount,
        affected_items: affectedItems,
        affected_services: [],
        savings: totalDiscountAmount.toFixed(2)
    };
    }

    // New helper method to calculate main discount for services without specific pricing
    async calculateMainDiscountForService(items, discount, condition) {
    if (!items.length) return 0;

    const requiredQty = parseInt(condition.qty) || 0;
    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);

    let applicableAmount = 0;

    if (condition.condition === 'equal_to' && totalQty >= requiredQty) {
        // Apply discount to exactly the required quantity
        applicableAmount = this.calculateBaseForQuantity(items, requiredQty);
    } else if (condition.condition === 'at_least' && totalQty >= requiredQty) {
        // Apply discount to all items
        applicableAmount = items.reduce((sum, item) => sum + (item.price_per_unit * item.quantity), 0);
    }

    if (applicableAmount === 0) return 0;

    // Apply the main discount amount and type
    switch (discount.amount_type) {
        case 'percent':
        return (applicableAmount * parseFloat(discount.amount)) / 100;
        case 'dollar':
        return Math.min(parseFloat(discount.amount), applicableAmount);
        case 'fixed':
        return Math.max(0, applicableAmount - parseFloat(discount.amount));
        default:
        return 0;
    }
    }

    // Helper method to get applicable quantity based on condition
    getApplicableQuantity(items, condition) {
    const requiredQty = parseInt(condition.qty) || 0;
    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);

    if (condition.condition === 'equal_to') {
        return Math.min(requiredQty, totalQty);
    } else if (condition.condition === 'at_least') {
        return totalQty;
    }

    return 0;
    }

    // Helper method to get items for a specific service ID
    getItemsForService(serviceId, cartState) {
    return cartState.items.filter(item =>
        String(item.category_id) === String(serviceId) ||
        String(item.service_term) === String(serviceId)
    );
    }

    // Updated calculateServiceDiscount to handle conditions properly
    async calculateServiceDiscount(items, priceConfig, condition) {
    if (!items.length) return 0;

    const requiredQty = parseInt(condition?.qty) || 0;
    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);

    let baseAmount = 0;

    // Determine how much to apply discount to based on condition
    if (condition) {
        if (condition.condition === 'equal_to' && totalQty >= requiredQty) {
        // Apply discount to exactly the required quantity
        baseAmount = this.calculateBaseForQuantity(items, requiredQty);
        } else if (condition.condition === 'at_least' && totalQty >= requiredQty) {
        // Apply discount to all items
        baseAmount = items.reduce((sum, item) => sum + (item.price_per_unit * item.quantity), 0);
        }
    } else {
        // No condition, apply to all items
        baseAmount = items.reduce((sum, item) => sum + (item.price_per_unit * item.quantity), 0);
    }

    if (baseAmount === 0) return 0;

    switch (priceConfig.type) {
        case 'percent':
        return (baseAmount * parseFloat(priceConfig.amount)) / 100;
        case 'dollar':
        return Math.min(parseFloat(priceConfig.amount), baseAmount);
        case 'fixed':
        return Math.max(0, baseAmount - parseFloat(priceConfig.amount));
        default:
        return 0;
    }
    }

    // Calculate free service discount (100% off for specified quantity)
    async calculateFreeServiceDiscount(items, condition) {
    const requiredQty = parseInt(condition.qty) || 0;
    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);

    if (condition.condition === 'equal_to' && totalQty >= requiredQty) {
        // Apply 100% discount to exactly the required quantity
        return this.calculateBaseForQuantity(items, requiredQty);
    } else if (condition.condition === 'at_least' && totalQty >= requiredQty) {
        // Apply 100% discount to all items
        return items.reduce((sum, item) => sum + (item.price_per_unit * item.quantity), 0);
    }

    return 0;
    }

    // Calculate regular discount without complex pricing
    async calculateRegularDiscount(discount, cartState) {
    // For category/item discounts with conditions, calculate based on specific items
    if (discount.type === 'category' && discount.conditions && Object.keys(discount.conditions).length > 0) {

        // Get applicable items for this discount
        const applicableItems = this.getApplicableItemsForDiscount(discount);

        if (applicableItems.length === 0) {
        return 0;
        }

        let totalDiscountAmount = 0;

        // Calculate discount based on conditions
        for (const [condId, condition] of Object.entries(discount.conditions)) {
        const matchingItems = applicableItems.filter(item =>
            condition.service === item.category ||
            String(condition.service_id) === String(item.category_id) ||
            String(condition.service_id) === String(item.service_term)
        );

        if (matchingItems.length > 0) {
            const totalQty = matchingItems.reduce((sum, item) => sum + item.quantity, 0);
            const condQty = parseInt(condition.qty) || 0;

            let applicableQuantity = 0;
            let baseForDiscount = 0;

            if (condition.condition === 'equal_to') {
            // For equal_to, only apply discount if we have at least the required quantity
            if (totalQty >= condQty) {
                applicableQuantity = condQty; // Only apply to the exact quantity required
                // Calculate base amount for exactly the required quantity
                baseForDiscount = this.calculateBaseForQuantity(matchingItems, applicableQuantity);
            }
            } else if (condition.condition === 'at_least') {
            if (totalQty >= condQty) {
                applicableQuantity = totalQty; // Apply to all items
                baseForDiscount = matchingItems.reduce((sum, item) => sum + (item.price_per_unit * item.quantity), 0);
            }
            }

            // Calculate discount amount based on type
            if (baseForDiscount > 0) {
            switch (discount.amount_type) {
                case 'percent':
                totalDiscountAmount += (baseForDiscount * parseFloat(discount.amount)) / 100;
                break;
                case 'dollar':
                totalDiscountAmount += Math.min(parseFloat(discount.amount), baseForDiscount);
                break;
                case 'fixed':
                // Fixed amount replaces the total price for the applicable quantity
                const fixedPrice = parseFloat(discount.amount);
                totalDiscountAmount += Math.max(0, baseForDiscount - fixedPrice);
                break;
            }
            }
        }
        }

        return totalDiscountAmount;
    } else {
        // For simple discounts without complex conditions, use the base subtotal
        const baseAmount = cartState.base_subtotal;

        switch (discount.amount_type) {
        case 'percent':
            return (baseAmount * parseFloat(discount.amount)) / 100;
        case 'dollar':
            return Math.min(parseFloat(discount.amount), baseAmount);
        case 'fixed':
            return Math.max(0, baseAmount - parseFloat(discount.amount));
        default:
            return 0;
        }
    }
    }

    // Helper method to get applicable items for a discount
    getApplicableItemsForDiscount(discount) {
    const applicableItems = [];

    if (discount.type === 'category') {
        for (const item of this.items) {
        if (discount.category && discount.category.includes(item.category)) {
            applicableItems.push(item);
        }
        }
    }

    return applicableItems;
    }

    // Helper method to calculate base amount for a specific quantity from matching items
    calculateBaseForQuantity(items, targetQuantity) {
    let remainingQty = targetQuantity;
    let totalBase = 0;

    // Sort by price per unit (descending) to apply discount to most expensive items first
    const sortedItems = [...items].sort((a, b) => b.price_per_unit - a.price_per_unit);

    for (const item of sortedItems) {
        if (remainingQty <= 0) break;

        const qtyToUse = Math.min(remainingQty, item.quantity);
        totalBase += qtyToUse * item.price_per_unit;
        remainingQty -= qtyToUse;
    }

    return totalBase;
    }

    // Fixed applyFinalCalculations method
    applyFinalCalculations(discountResults) {
    const totalDiscountAmount = discountResults.totalDiscount;
    this.appliedDiscounts = discountResults.appliedDiscounts;

    // Calculate what amount after discounts counts toward minimum
    const discountedMinimumTotal = Math.max(0, this._minimumAmountTotal - totalDiscountAmount);

    // Recalculate if minimum is met after discounts
    this.minimumAmountMet = discountedMinimumTotal >= this.minimumAmount;

    // Calculate how much more is needed to meet minimum
    this.minimumAmountNeeded = Math.max(0, this.minimumAmount - discountedMinimumTotal);

    const discountedSubtotal = Math.max(0, this.actual_subtotal - totalDiscountAmount);

    if (this.minimumAmountMet) {
        this.subtotal = discountedSubtotal;
        this.taxes = this.calculateTax(this.actual_subtotal - totalDiscountAmount);
    } else {
        // When minimum not met, calculate the total including excluded items
        const excludedItemsTotal = this.actual_subtotal - this._minimumAmountTotal;
        this.subtotal = this.minimumAmount + excludedItemsTotal;
        this.taxes = this.calculateTax(this.minimumAmount);
    }

    this.total = this.subtotal + this.taxes;
    }

    // Helper methods
    async getCartState() {
    return {
        items: this.items,
        discounts: this.discounts,
        categorySummary: this.getCategorySummary(),
        base_subtotal: this.base_subtotal,
        actual_subtotal: this.actual_subtotal
    };
    }

    async canAddDiscount(discount) {
    // Basic combinability check - can be enhanced
    const existingOverride = this.discounts.some(d =>
        d.override === true || d.override === "1"
    );

    if (existingOverride && !(discount.override === true || discount.override === "1")) {
        return false;
    }

    return true;
    }

    async waitForUpdate() {
    return new Promise(resolve => {
        const check = () => {
        if (!this._isUpdating) {
            resolve();
        } else {
            setTimeout(check, 10);
        }
        };
        check();
    });
    }

    // Utility methods (kept from original)
    normalizeItem(item) {
    if (!item.item_id) {
        this.logger.error("Each item must have an item_id.");
        return null;
    }
    if (!item.category) {
        this.logger.error("Each product must have a category.");
        return null;
    }

    const normalized = {
        ...item,
        price_per_unit: parseFloat(item.price_per_unit) || 0,
        quantity: Math.max(parseInt(item.quantity, 10) || 0, 0),
        discountable: item.discountable !== false,
        taxable: item.taxable !== false,
        exclude_from_min: item.exclude_from_min === true,
        addons: this.normalizeAddons(item.addons),
        subtotal: 0,
        base_price: 0,
        discount_amount: 0,
        price_with_discount: 0
    };

    // Ensure service_term is included if provided
    if (item.service_term !== undefined) {
        normalized.service_term = item.service_term;
    }
    if (item.category_id !== undefined) {
        normalized.category_id = item.category_id;
    }

    // Calculate initial subtotal
    normalized.subtotal = normalized.price_per_unit * normalized.quantity;
    normalized.base_price = normalized.subtotal;

    return normalized;
    }

    normalizeAddons(addons) {
    if (!Array.isArray(addons)) return [];

    return addons.map(addon => ({
        ...addon,
        addon_price: parseFloat(addon.addon_price) || 0,
        quantity: parseInt(addon.quantity, 10) || 0,
        discountable: addon.discountable !== false,
        taxable: addon.taxable !== false,
        exclude_from_min: addon.exclude_from_min === true,
        base_price: 0,
        discount_amount: 0,
        price_with_discount: 0
    }));
    }

    normalizeDiscount(discount) {
    return {
        ...discount,
        amount: parseFloat(discount.amount) || 0,
        min_amount: parseFloat(discount.min_amount) || 0,
        override: discount.override === true || discount.override === "1",
        non_combinable: discount.non_combinable === true || discount.non_combinable === "1"
    };
    }

    mergeAddons(item, newAddons) {
    newAddons.forEach(newAddon => {
        const existingAddon = item.addons.find(a => a.addon_id === newAddon.addon_id);
        if (existingAddon) {
        existingAddon.quantity += newAddon.quantity;
        } else {
        item.addons.push(newAddon);
        }
    });
    }

    getItem(itemId) {
    return this.items.find(item => item.item_id === itemId);
    }

    itemAdded(itemId) {
    return this.items.some(item => item.item_id === itemId);
    }

    discountAdded(discountId) {
    return this.discounts.some(d => d.id === discountId || d.promo_code === discountId);
    }

    calculateTax(taxableAmount) {
    if (!this.tax || taxableAmount <= 0) return 0;
    return taxableAmount * (this.tax / 100);
    }

    setTax(taxRate) {
    this.tax = parseFloat(taxRate) || 0;
    }

    setMinimumAmount(amount) {
    this.minimumAmount = parseFloat(amount) || 0;
    }

    getCategorySummary() {
    const summary = {};

    this.items.forEach(item => {
        let category = item.category || 'Uncategorized';

        if (!summary[category]) {
        summary[category] = {
            text: category,
            original_price: 0,
            discounted_price: 0,
            quantity: 0,
            subtotal: 0,
            addons: {},
            items: []
        };
        }

        const catData = summary[category];
        catData.original_price += item.base_price || 0;
        catData.discounted_price += item.price_with_discount || 0;
        catData.quantity += item.quantity || 0;
        catData.subtotal += item.subtotal || 0;
        catData.items.push({ ...item });

        // Process addons
        item.addons?.forEach(addon => {
        const addonCat = addon.category || 'Addon';

        if (!catData.addons[addonCat]) {
            catData.addons[addonCat] = {
            text: addonCat,
            original_price: 0,
            discounted_price: 0,
            quantity: 0,
            subtotal: 0
            };
        }

        const addonData = catData.addons[addonCat];
        addonData.original_price += addon.base_price || 0;
        addonData.discounted_price += addon.price_with_discount || 0;
        addonData.quantity += addon.quantity || 0;
        addonData.subtotal += addon.subtotal || 0;
        });
    });

    return summary;
    }

    getCartSummary() {
    return {
        items: this.items,
        discounts: this.discounts,
        appliedDiscounts: this.appliedDiscounts || [],
        actual_subtotal: this.formatMoney(this.actual_subtotal),
        subtotal: this.formatMoney(this.subtotal),
        itemCount: this.itemCount,
        tax: this.formatMoney(this.tax),
        taxes: this.formatMoney(this.taxes),
        total: this.formatMoney(this.total),
        minimumAmount: this.formatMoney(this.minimumAmount),
        minimumAmountMet: this.minimumAmountMet,
        minimumAmountNeeded: this.minimumAmountNeeded,
        base_subtotal: this.formatMoney(this.base_subtotal),
        totalSavings: this.formatMoney(
        this.appliedDiscounts?.reduce((sum, d) => sum + d.discount_amount, 0) || 0
        )
    };
    }

    getAppliedDiscounts() {
    return this.appliedDiscounts || [];
    }

    formatMoney(number) {
    return parseFloat(number || 0).toFixed(2);
    }

    clearCache() {
    this._cache.clear();
    }

    resetCart() {
    this.items = [];
    this.discounts = [];
    this.subtotal = 0;
    this.base_subtotal = 0;
    this.itemCount = 0;
    this.taxes = 0;
    this.total = 0;
    this.minimumAmountMet = true;
    this.actual_subtotal = 0;
    this.minimumAmountNeeded = 0;
    this.tax = 0;
    this.minimumAmount = 0;
    this.clearCache();
    }

    // Additional methods for compatibility with CartHelper

    // Get minimum amount difference
    getMinimumDifference() {
    if (this.minimumAmountMet) return 0;
    return Math.max(0, this.minimumAmountNeeded);
    }

    // Get categories list
    getCategories() {
    return [...new Set(this.items.map(item => item.category))];
    }

    // Get items by category
    getItemsByCategory(category) {
    return this.items.filter(item => item.category === category);
    }

    // Get total savings amount
    getTotalSavings() {
    const savings = this.appliedDiscounts?.reduce((sum, d) => sum + d.discount_amount, 0) || 0;
    return this.formatMoney(savings);
    }

    // Force update method
    async forceUpdate() {
    this.clearCache();
    await this.updateCartTotals();
    }

    // Check if minimum amount is met
    isMinimumAmountMet() {
    return this.minimumAmountMet;
    }

    // Load from storage (for compatibility)
    loadFromStorage() {
    // AsyncCart doesn't use storage by default, but this maintains compatibility
    // You can implement localStorage logic here if needed
    }

    // Save to storage (for compatibility)
    saveToStorage() {
    // AsyncCart doesn't use storage by default, but this maintains compatibility
    // You can implement localStorage logic here if needed
    }

    // Set quantity method for compatibility
    async setQuantity(type, itemId, quantity, addonId = null) {
    quantity = parseInt(quantity, 10);
    if (isNaN(quantity) || quantity < 0) {
        throw new Error("Quantity must be a non-negative integer");
    }

    if (type === 'item') {
        return await this.updateItem(itemId, { quantity });
    } else if (type === 'addon' && addonId) {
        const item = this.getItem(itemId);
        if (!item) {
        throw new Error(`Item with ID ${itemId} not found`);
        }

        const addon = item.addons?.find(a => a.addon_id === addonId);
        if (!addon) {
        throw new Error(`Addon with ID ${addonId} not found`);
        }

        addon.quantity = quantity;
        await this.updateCartTotals();
        return true;
    } else {
        throw new Error("Invalid type or missing addonId");
    }
    }

    // Add addon to item
    async addItemAddon(itemId, addon) {
    const item = this.getItem(itemId);
    if (!item) {
        throw new Error(`Item with ID ${itemId} not found`);
    }

    if (!item.addons) item.addons = [];

    const normalizedAddon = {
        ...addon,
        addon_price: parseFloat(addon.addon_price) || 0,
        quantity: parseInt(addon.quantity, 10) || 0,
        discountable: addon.discountable !== false,
        taxable: addon.taxable !== false,
        exclude_from_min: addon.exclude_from_min === true
    };

    const existingAddon = item.addons.find(a => a.addon_id === normalizedAddon.addon_id);
    if (existingAddon) {
        existingAddon.quantity += normalizedAddon.quantity;
    } else {
        item.addons.push(normalizedAddon);
    }

    await this.updateCartTotals();
    return true;
    }

    // Remove addon from item
    async removeItemAddon(itemId, addonId) {
    const item = this.getItem(itemId);
    if (!item) {
        throw new Error(`Item with ID ${itemId} not found`);
    }

    const addonIndex = item.addons?.findIndex(a => a.addon_id === addonId);
    if (addonIndex === -1 || addonIndex === undefined) {
        throw new Error(`Addon with ID ${addonId} not found`);
    }

    item.addons.splice(addonIndex, 1);
    await this.updateCartTotals();
    return true;
    }

    // Update addon properties
    async updateItemAddon(itemId, addonId, updatedProperties) {
    const item = this.getItem(itemId);
    if (!item) {
        throw new Error(`Item with ID ${itemId} not found`);
    }

    const addon = item.addons?.find(a => a.addon_id === addonId);
    if (!addon) {
        throw new Error(`Addon with ID ${addonId} not found`);
    }

    Object.assign(addon, updatedProperties);
    await this.updateCartTotals();
    return true;
    }
}
